import React, { useMemo } from 'react';
import {
	Checkbox,
	Col,
	ConfigProvider,
	Empty,
	Form,
	Input,
	Row,
	Space,
	Typography,
} from 'antd';
import classNames from 'classnames';
import {
	CargoUnitSingularLabels,
	LaytimeIntervals,
	LaytimeCalculationMethods,
	EstimateStatus,
	LaytimeTerms,
	CargoContractSides,
} from '@shared/utils/constants';
import { formatNumber } from '@shared/utils/formatNumber';
import { isMissingOrZero } from '@shared/utils/number';
import { getDemurrageTermLabel } from '@shared/utils/demurrageTerm';
import type { CargoPortProps } from '@api/models/cargo-port';
import type { EditableCargo } from '@api/features/cargos/updateCargo';
import type { CargoPortWithEstimatedValues } from '@api/features/cargos/transformCargos';
import NumericInput from '@client/components/NumericInput';
import Select from '@client/components/Select';
import TooltipIcon from '@client/components/TooltipIcon';
import DatePicker, { DatePickerValue } from '@client/components/DatePicker';
import { SPLIT_BL_TOOLTIP } from '../helpers/cargoUtils';
import { ModalModes } from '../details/CargoDetailsScreen';
import styles from './CargosListScreen.module.css';

const CargoPortForm = ({
	mode,
	cargoPort,
	type,
	cargo,
	enableDemDes,
	quantitiesRemaining,
	handleCargoPortChange,
}: {
	mode: ModalModes;
	cargoPort: (CargoPortProps & { hasSplitBl?: boolean }) | undefined;
	type: 'loadingPort' | 'dischargePort';
	enableDemDes: boolean;
	cargo: EditableCargo;
	quantitiesRemaining: { loadedRemaining: number; dischargeRemaining: number } | null;
	refreshCargoDetails: () => void;
	handleCargoPortChange: (values: {
		cargoPortId: number; cargoId: number; attributes: any;
	}
	) => void;
}) => {
	const creatingCargo = mode === 'creatingOnContract';
	const interval = cargoPort?.laytimeInterval ?? LaytimeIntervals.DAY;
	const intervalLabel = interval === LaytimeIntervals.DAY ? 'D' : 'H';
	const differentUnits = cargo.unit !== cargo.secondaryUnit;
	const { componentDisabled } = ConfigProvider.useConfig();
	const disabled = componentDisabled || (cargo?.status === EstimateStatus.Fixed && cargo.voyageId == null);
	const hasCargoRelet = cargo.contractSide === CargoContractSides.VC_IN;
	const manualTimeAllowed = cargoPort?.timeAllowedOverride != null;
	const hasSplitBl = cargoPort?.hasSplitBl === true;
	const demurrageWord = getDemurrageTermLabel(cargo.demurrageTerm);

	const quantityRemaining = useMemo(() => {
		if (quantitiesRemaining == null) {
			return 0;
		}

		if (type === 'loadingPort') {
			return quantitiesRemaining.loadedRemaining;
		}

		return quantitiesRemaining.dischargeRemaining;
	}, [quantitiesRemaining, type]);

	const calculationMethodOptions = useMemo(() => {
		const options = Object.values(LaytimeCalculationMethods).map((v) => ({
			value: v,
			label: v,
		}));

		const existing = cargoPort?.calculationMethod;

		if (existing == null) {
			return options;
		}

		const newestCalculationMethods = Object.values(LaytimeCalculationMethods);

		if (newestCalculationMethods.includes(existing)) {
			return options;
		}

		return [...options, { value: existing, label: existing, disabled: true }];
	}, [cargoPort]);

	if (cargoPort == null) {
		return (<Empty description="No cargo-port selected" />);
	}

	const handleUpdate = (
		fieldName: keyof CargoPortWithEstimatedValues,
		value: number | string | boolean | DatePickerValue | null,
	) => {
		handleCargoPortChange({
			cargoPortId: cargoPort.id,
			cargoId: cargo.id,
			attributes: {
				[fieldName]: value,
			},
		});
	};

	return (
		<>
			<Row gutter={[16, 0]}>
				<Col span={12}>
					<Form.Item
						valuePropName="v"
						name="cpQuantity"
						required={creatingCargo}
						label={(
							<Row
								className={styles.quantityRemainingContainer}
							>
								<p>
									{type === 'loadingPort' ? 'Amount Loaded' : 'Amount Discharged'}
								</p>
								<Typography.Text
									// @ts-ignore
									italic
									className={classNames(styles.quantityRemaining, {
										[styles.redText]: quantityRemaining < 0,
									})}
								>
									(Remaining:
									{` ${formatNumber(quantityRemaining, { separateThousands: true })} ${cargo.unit}`}
									)
								</Typography.Text>
							</Row>
						)}
						colon={false}
						className={styles.formItemLabel}
						labelCol={{ span: 24 }}
					>
						<NumericInput
							key={cargoPort.id}
							data-testid="cp-quantity"
							placeholder={type === 'loadingPort' ? 'Amount Loaded' : 'Amount Discharged'}
							addonAfter={CargoUnitSingularLabels[cargo.unit] ?? 'MT'}
							value={cargoPort.cpQuantity}
							onChange={(value) => handleUpdate('cpQuantity', value)}
							disabled={disabled || manualTimeAllowed || (type === 'loadingPort' && hasCargoRelet)}
						/>
					</Form.Item>
				</Col>
				{differentUnits && (
					<Col span={12}>
						<Form.Item
							valuePropName="v"
							name="secondaryCpQuantity"
							label={(
								<Row>
									<p>
										{type === 'loadingPort' ? 'Amount Loaded (secondary)' : 'Amount Discharged (secondary)'}
									</p>
								</Row>
							)}
							colon={false}
							className={styles.formItemLabel}
							labelCol={{ span: 24 }}
						>
							<NumericInput
								key={cargoPort.id}
								placeholder={type === 'loadingPort' ? 'Amount Loaded (secondary)' : 'Amount Discharged (secondary)'}
								addonAfter={CargoUnitSingularLabels[cargo.secondaryUnit] ?? 'MT'}
								value={cargoPort.secondaryCpQuantity}
								onChange={(value) => handleUpdate('secondaryCpQuantity', value)}
								disabled={disabled || hasCargoRelet}
							/>
						</Form.Item>
					</Col>
				)}
				{cargo.laytimeTerms === LaytimeTerms.NON_REVERSIBLE && (
					<Col span={7}>
						<Form.Item
							valuePropName="v"
							name="loadingRate"
							label={type === 'loadingPort' ? 'Loading Rate' : 'Discharge Rate'}
							colon={false}
							className={styles.formItemLabel}
							labelCol={{ span: 24 }}
						>
							<NumericInput
								key={cargoPort.id}
								addonAfter={intervalLabel}
								placeholder={`${CargoUnitSingularLabels[cargo.unit] ?? 'MT'} /${intervalLabel}`}
								value={cargoPort.loadingRate}
								onChange={(value) => handleUpdate('loadingRate', value)}
								disabled={disabled || manualTimeAllowed}
							/>
						</Form.Item>
					</Col>
				)}
				<Col span={5}>
					<Form.Item
						required={creatingCargo}
						valuePropName="v"
						name="laytimeInterval"
						label="Interval"
						initialValue={LaytimeIntervals.DAY}
						colon={false}
						className={styles.formItemLabel}
						labelCol={{ span: 24 }}
					>
						<Select
							options={[{
								label: 'D',
								value: 'day',
							},
							{
								label: 'H',
								value: 'hour',
							}]}
							data-testid="laytime-interval"
							value={cargoPort.laytimeInterval}
							key={cargoPort.id}
							onChange={(value) => handleUpdate('laytimeInterval', value)}
							showSearch
							disabled={disabled}
						/>
					</Form.Item>
				</Col>
				<>
					<Col span={12}>
						<Form.Item
							valuePropName="h"
							name="BL Quantity"
							label={(
								<Row>
									<Typography>BL Quantity</Typography>
									{hasSplitBl && (
										<TooltipIcon>
											{SPLIT_BL_TOOLTIP}
										</TooltipIcon>
									)}
								</Row>
							)}
							className={styles.formItemLabel}
							labelCol={{ span: 24 }}
							colon={false}
						>
							<NumericInput
								key={cargoPort.id}
								addonAfter={CargoUnitSingularLabels[cargo.unit] ?? 'MT'}
								placeholder="BL Quantity"
								value={cargoPort.blQuantity}
								onChange={(value) => handleUpdate('blQuantity', value)}
								disabled={disabled || hasCargoRelet || hasSplitBl}
							/>
						</Form.Item>
					</Col>
					{differentUnits && (
						<Col span={12}>
							<Form.Item
								valuePropName="h"
								name="BL Quantity (secondary)"
								label="BL Quantity (secondary)"
								className={styles.formItemLabel}
								labelCol={{ span: 24 }}
								colon={false}
							>
								<NumericInput
									key={cargoPort.id}
									addonAfter={CargoUnitSingularLabels[cargo.secondaryUnit] ?? 'MT'}
									placeholder="BL Quantity (secondary)"
									value={cargoPort.secondaryBlQuantity}
									onChange={(value) => handleUpdate('secondaryBlQuantity', value)}
									disabled={disabled || hasCargoRelet}
								/>
							</Form.Item>
						</Col>
					)}
					<Col span={12}>
						<Form.Item
							valuePropName="h"
							name="BL Date"
							label="BL Date"
							className={styles.formItemLabel}
							labelCol={{ span: 24 }}
							colon={false}
						>
							<DatePicker
								key={cargoPort.id}
								className={styles.fullWidth}
								placeholder="Select BL date"
								value={cargoPort.blDate ? cargoPort.blDate : null}
								onChange={(value) => handleUpdate('blDate', value)}
								disabled={disabled || hasCargoRelet}
							/>
						</Form.Item>
					</Col>
				</>
				{type === 'loadingPort' && (
					<>
						<Col span={12}>
							<Form.Item
								valuePropName="v"
								name="laycanFrom"
								label="Laycan From"
								className={styles.formItemLabel}
								labelCol={{ span: 24 }}
								colon={false}
							>
								<DatePicker
									key={cargoPort.id}
									className={styles.fullWidth}
									placeholder="Select laycan from"
									tabIndex={13}
									value={cargoPort.laycanFrom}
									onChange={(value) => handleUpdate('laycanFrom', value)}
									disabled={disabled}
									time
									showTimezone={false}
								/>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								valuePropName="v"
								name="laycanTo"
								label="Laycan To"
								className={styles.formItemLabel}
								labelCol={{ span: 24 }}
								colon={false}
							>
								<DatePicker
									key={cargoPort.id}
									className={styles.fullWidth}
									placeholder="Select laycan to"
									tabIndex={14}
									value={cargoPort.laycanTo}
									onChange={(value) => handleUpdate('laycanTo', value)}
									disabled={disabled}
									time
									showTimezone={false}
								/>
							</Form.Item>
						</Col>
					</>
				)}
				{enableDemDes && (
					<>
						<Col span={12}>
							<Form.Item
								valuePropName="v"
								name="demurrage"
								key={cargoPort.id}
								colon={false}
								label={(
									<Row>
										<Typography>{demurrageWord} Rate</Typography>
										{!enableDemDes && (
											<TooltipIcon>
												When laytime terms are not non-reversible,
												refer to {demurrageWord.toLowerCase()} on the cargo itself.
											</TooltipIcon>
										)}
									</Row>
								)}
								className={styles.formItemLabel}
								labelCol={{ span: 24 }}
							>
								<NumericInput
									addonAfter={`${cargo.currency}/${cargo.demurrageDespatchInterval}`}
									placeholder={demurrageWord}
									disabled={!enableDemDes || disabled}
									key={cargoPort.id}
									value={cargoPort.demurrage}
									onChange={(value) => handleUpdate('demurrage', value)}
									isRequiredHighlightBorder={isMissingOrZero(cargoPort.demurrage)}
								/>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								valuePropName="v"
								name="despatch"
								colon={false}
								label={(
									<Row>
										<Typography>Despatch Rate</Typography>
										{!enableDemDes && (
											<TooltipIcon>
												When laytime terms are not non-reversible,
												refer to despatch on the cargo itself.
											</TooltipIcon>
										)}
									</Row>
								)}
								className={styles.formItemLabel}
								labelCol={{ span: 24 }}
							>
								<NumericInput
									addonAfter={`${cargo.currency}/${cargo.demurrageDespatchInterval}`}
									placeholder="Despatch"
									disabled={!enableDemDes || disabled}
									key={cargoPort.id}
									value={cargoPort.despatch}
									onChange={(value) => handleUpdate('despatch', value)}
									isRequiredHighlightBorder={isMissingOrZero(cargoPort.despatch)}
								/>
							</Form.Item>
						</Col>
					</>
				)}
				{cargo.laytimeTerms === LaytimeTerms.NON_REVERSIBLE && (
					<>
						<Col span={12}>
							<Form.Item
								valuePropName="v"
								name="timeAllowed"
								label="Calculated Time Allowed"
								colon={false}
								className={styles.formItemLabel}
								labelCol={{ span: 24 }}
							>
								<NumericInput
									disabled
									placeholder="Time allowed"
									addonAfter={intervalLabel}
									key={cargoPort.id}
									value={cargoPort.estimatedTimeAllowed}
									onChange={(value) => handleUpdate('estimatedTimeAllowed', value)}
								/>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								valuePropName="v"
								name="timeAllowedOverride"
								label="Input Time Allowed"
								colon={false}
								className={styles.formItemLabel}
								labelCol={{ span: 24 }}
							>
								<NumericInput
									placeholder="Time allowed"
									addonAfter={intervalLabel}
									key={cargoPort.id}
									value={cargoPort.timeAllowedOverride}
									onChange={(value) => handleUpdate('timeAllowedOverride', value)}
								/>
							</Form.Item>
						</Col>
					</>
				)}
				<Col span={12}>
					<Form.Item
						required={creatingCargo}
						valuePropName="v"
						name="calculationMethod"
						label="Calculation Method"
						className={styles.formItemLabel}
						labelCol={{ span: 24 }}
						colon={false}
					>
						<Select
							options={calculationMethodOptions}
							placeholder="Select Method"
							key={cargoPort.id}
							value={cargoPort.calculationMethod}
							onChange={(value) => handleUpdate('calculationMethod', value)}
							defaultValue={LaytimeCalculationMethods.SHINC}
							showSearch
							disabled={disabled}
						/>
					</Form.Item>
					<Form.Item>
						<Row gutter={8} align="middle">
							<Col>
								<Space>
									STS Ops
									<Checkbox
										title="STS Ops"
										checked={cargoPort.useStsOps ?? false}
										onClick={() => handleUpdate('useStsOps', !cargoPort.useStsOps)}
										disabled={disabled}
									/>
								</Space>
							</Col>
							{cargoPort.useStsOps && (
								<Col flex="auto">
									<Input
										placeholder="Mother vessel"
										value={cargoPort.stsOpsMotherVessel ?? ''}
										onChange={(e) => handleUpdate('stsOpsMotherVessel', e.currentTarget.value)}
										disabled={disabled}
									/>
								</Col>
							)}
						</Row>
					</Form.Item>
				</Col>
			</Row>
		</>
	);
};

export default CargoPortForm;
